export const PORTAL_MOBILE_APP_SHELL_CLASS = 'portal-mobile-app-shell';

const ENABLED_VALUES = new Set(['1', 'true', 'yes', 'app', 'mobile-app', 'standalone']);
const DISABLED_VALUES = new Set(['0', 'false', 'no', 'browser']);
const MOBILE_APP_SHELL_PARAMS = ['portalMobileShell', 'openteamMobileShell'];
const MOBILE_APP_SOURCE_VALUES = new Set(['openteam-mobile', 'openteam-ios', 'openteam-android']);

export type PortalMobileAppShellSignals = {
	search?: string | null;
	standalone?: unknown;
	displayModeStandalone?: boolean;
	displayModeFullscreen?: boolean;
	userAgent?: string | null;
	maxTouchPoints?: number;
	hasReactNativeWebView?: boolean;
	hasCapacitor?: boolean;
	hasCordova?: boolean;
	hasWebkitMessageHandlers?: boolean;
};

const normalizeFlag = (value?: string | null): boolean | null => {
	const normalized = String(value || '')
		.trim()
		.toLowerCase();
	if (!normalized) {
		return null;
	}
	if (ENABLED_VALUES.has(normalized)) {
		return true;
	}
	if (DISABLED_VALUES.has(normalized)) {
		return false;
	}
	return null;
};

const readMobileAppShellSearchOverride = (search?: string | null): boolean | null => {
	const rawSearch = String(search || '').trim();
	if (!rawSearch) {
		return null;
	}

	const params = new URLSearchParams(rawSearch.startsWith('?') ? rawSearch.slice(1) : rawSearch);
	for (const key of MOBILE_APP_SHELL_PARAMS) {
		const value = normalizeFlag(params.get(key));
		if (value != null) {
			return value;
		}
	}

	const source = String(params.get('source') || params.get('utm_source') || '')
		.trim()
		.toLowerCase();
	return MOBILE_APP_SOURCE_VALUES.has(source) ? true : null;
};

const isIOSWebViewUserAgent = (userAgent: string, maxTouchPoints = 0): boolean => {
	const isiOSDevice =
		/\b(iPad|iPhone|iPod)\b/i.test(userAgent) ||
		(/\bMacintosh\b/i.test(userAgent) && maxTouchPoints > 1);
	const isWebKitMobile = /\bAppleWebKit\b/i.test(userAgent) && /\bMobile\//i.test(userAgent);
	const hasBrowserToken = /\b(Safari|CriOS|FxiOS|EdgiOS|OPiOS)\//i.test(userAgent);
	return isiOSDevice && isWebKitMobile && !hasBrowserToken;
};

const isAndroidWebViewUserAgent = (userAgent: string): boolean =>
	/\bAndroid\b/i.test(userAgent) &&
	(/; wv\)/i.test(userAgent) || /\bVersion\/\d+(?:\.\d+)?\b.*\bChrome\//i.test(userAgent));

const isBrandedMobileAppUserAgent = (userAgent: string): boolean =>
	/\b(OpenTeamMobile|OpenTeam-iOS|OpenTeam-Android|openteam-mobile)\b/i.test(userAgent);

export const isPortalMobileAppShell = (signals: PortalMobileAppShellSignals): boolean => {
	const searchOverride = readMobileAppShellSearchOverride(signals.search);
	if (searchOverride != null) {
		return searchOverride;
	}

	if (signals.standalone === true || signals.displayModeStandalone || signals.displayModeFullscreen) {
		return true;
	}

	if (
		signals.hasReactNativeWebView ||
		signals.hasCapacitor ||
		signals.hasCordova ||
		signals.hasWebkitMessageHandlers
	) {
		return true;
	}

	const userAgent = String(signals.userAgent || '').trim();
	if (!userAgent) {
		return false;
	}

	return (
		isBrandedMobileAppUserAgent(userAgent) ||
		isIOSWebViewUserAgent(userAgent, signals.maxTouchPoints) ||
		isAndroidWebViewUserAgent(userAgent)
	);
};

export const detectPortalMobileAppShell = (win: Window): boolean => {
	const runtimeWindow = win as Window & {
		ReactNativeWebView?: unknown;
		Capacitor?: unknown;
		cordova?: unknown;
		webkit?: { messageHandlers?: Record<string, unknown> };
	};
	const runtimeNavigator = win.navigator as Navigator & { standalone?: boolean };
	const displayModeMatches = (mode: string): boolean => {
		try {
			return win.matchMedia?.(`(display-mode: ${mode})`)?.matches ?? false;
		} catch {
			return false;
		}
	};
	const messageHandlers = runtimeWindow.webkit?.messageHandlers;

	return isPortalMobileAppShell({
		search: win.location.search,
		standalone: runtimeNavigator.standalone,
		displayModeStandalone: displayModeMatches('standalone'),
		displayModeFullscreen: displayModeMatches('fullscreen'),
		userAgent: runtimeNavigator.userAgent,
		maxTouchPoints: runtimeNavigator.maxTouchPoints,
		hasReactNativeWebView: Boolean(runtimeWindow.ReactNativeWebView),
		hasCapacitor: Boolean(runtimeWindow.Capacitor),
		hasCordova: Boolean(runtimeWindow.cordova),
		hasWebkitMessageHandlers: Boolean(
			messageHandlers && Object.keys(messageHandlers).length > 0
		)
	});
};

export const syncPortalMobileAppShellClass = (win: Window, doc: Document): boolean => {
	const enabled = detectPortalMobileAppShell(win);
	doc.documentElement.classList.toggle(PORTAL_MOBILE_APP_SHELL_CLASS, enabled);
	return enabled;
};
